import { useState } from "react";
import { DollarSign, FileSearch, LockOpen, Package } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import { Spinner } from "@workspace/ui/components/spinner";
import { cn } from "@workspace/ui/lib/utils";

import type { OpenDrawerReason } from "../../types";
import { useEposContext } from "../epos-context";

interface OpenCashDrawerDialogProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	drawerId: string;
}

const reasons: {
	value: OpenDrawerReason;
	label: string;
	icon: React.ReactNode;
	description: string;
}[] = [
	{
		value: "Float",
		label: "Float",
		icon: <DollarSign className="size-5" />,
		description: "Add or adjust float amount",
	},
	{
		value: "Payout",
		label: "Payout",
		icon: <Package className="size-5" />,
		description: "Cash payout for expenses",
	},
	{
		value: "Audit",
		label: "Audit",
		icon: <FileSearch className="size-5" />,
		description: "Cash count or audit check",
	},
	{
		value: "Other",
		label: "Other",
		icon: <LockOpen className="size-5" />,
		description: "Other reason (specify below)",
	},
];

export function OpenCashDrawerDialog({
	isOpen,
	onOpenChange,
	drawerId,
}: OpenCashDrawerDialogProps) {
	const [selectedReason, setSelectedReason] = useState<OpenDrawerReason | null>(
		null,
	);
	const [reasonText, setReasonText] = useState("");
	const { openCashDrawer, isOpeningCashDrawer } = useEposContext();

	const handleClose = () => {
		setSelectedReason(null);
		setReasonText("");
		onOpenChange(false);
	};

	const handleSubmit = async () => {
		if (!selectedReason) {
			toast.error("Please select a reason");
			return;
		}

		if (selectedReason === "Other" && !reasonText.trim()) {
			toast.error("Please provide a reason description");
			return;
		}

		try {
			await openCashDrawer({
				drawerId,
				reason: selectedReason,
				reasonText: selectedReason === "Other" ? reasonText.trim() : null,
			});
			// Success handling (toast + dialog close) is done in the context
			setSelectedReason(null);
			setReasonText("");
		} catch {
			// Error handling is done in the context
		}
	};

	const isValid =
		selectedReason && (selectedReason !== "Other" || reasonText.trim());

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Open Cash Drawer</DialogTitle>
					<DialogDescription>
						Select a reason for opening the cash drawer without a sale. This
						action will be recorded.
					</DialogDescription>
				</DialogHeader>

				<div className="grid grid-cols-2 gap-3 py-4">
					{reasons.map((reason) => (
						<button
							key={reason.value}
							type="button"
							onClick={() => setSelectedReason(reason.value)}
							className={cn(
								"flex flex-col items-center gap-2 rounded-lg border p-4 text-center transition-colors hover:bg-accent",
								selectedReason === reason.value && "border-primary bg-accent",
							)}
						>
							<div
								className={cn(
									"flex size-10 items-center justify-center rounded-full bg-muted",
									selectedReason === reason.value &&
										"bg-primary text-primary-foreground",
								)}
							>
								{reason.icon}
							</div>
							<div className="font-medium">{reason.label}</div>
							<div className="text-xs text-muted-foreground">
								{reason.description}
							</div>
						</button>
					))}
				</div>

				{selectedReason === "Other" && (
					<div className="space-y-2">
						<Label htmlFor="reason-text">Reason Description</Label>
						<Input
							id="reason-text"
							placeholder="Enter reason for opening drawer..."
							value={reasonText}
							onChange={(e) => setReasonText(e.target.value)}
							maxLength={500}
						/>
					</div>
				)}

				<DialogFooter className="gap-3 flex-col sm:flex-row">
					<Button
						variant="outline"
						onClick={handleClose}
						className="h-12 text-base"
					>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!isValid || isOpeningCashDrawer}
						className="h-12 text-base"
					>
						{isOpeningCashDrawer && <Spinner />}
						Open Drawer
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
